import { DeleteOutlined, InfoCircleOutlined, LeftOutlined, PlusOutlined } from "@ant-design/icons";
import { getClientEnv } from "@shared/env";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Checkbox, Form, Image, Input, InputNumber, message, Select, Upload } from "antd";
import { useEffect, useState } from "react";
import {
	Navigate,
	useLocation,
	useNavigate,
	useOutletContext,
	useSearchParams,
} from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { parseApiError } from "@/shared/utils/error";
import { blocksApi } from "../api/blocks.api";
import { buildingsApi } from "../api/buildings.api";
import { filesApi } from "../api/files.api";
import { roomsApi } from "../api/rooms.api";
import { sectionsApi } from "../api/sections.api";
import { calculatePriceRemont, toBackendPricePerArea } from "../utils/room-price";
import { getUploadedAssetUrl } from "../utils/uploaded-asset-url";

const env = getClientEnv();

type RoomUploadFieldProps = {
	existingAsset?: string;
	file?: File;
	label: string;
	onFileChange: (file: File | undefined) => void;
};

function RoomUploadField({ existingAsset, file, label, onFileChange }: RoomUploadFieldProps) {
	const [previewUrl, setPreviewUrl] = useState<string>();

	useEffect(() => {
		if (file) {
			const url = URL.createObjectURL(file);
			setPreviewUrl(url);
			return () => URL.revokeObjectURL(url);
		}
		if (existingAsset) {
			setPreviewUrl(getUploadedAssetUrl(env.VITE_API_URL, existingAsset));
		} else {
			setPreviewUrl(undefined);
		}
	}, [file, existingAsset]);

	return (
		<Form.Item label={label} style={{ marginBottom: "12px" }}>
			<div className="crm-square-upload-wrapper">
				{previewUrl ? (
					<div className="crm-square-upload-preview">
						<Image
							src={previewUrl}
							alt={label}
							style={{ width: "96px", height: "96px", objectFit: "cover", borderRadius: "12px" }}
							wrapperStyle={{ width: "96px", height: "96px" }}
						/>
						<Button
							type="text"
							icon={<DeleteOutlined style={{ color: "var(--crm-red)" }} />}
							onClick={(e) => {
								e.stopPropagation();
								onFileChange(undefined);
							}}
							className="crm-square-upload-delete"
						/>
					</div>
				) : (
					<Upload
						accept="image/png,image/jpeg,image/webp"
						beforeUpload={(nextFile) => {
							onFileChange(nextFile);
							return false;
						}}
						maxCount={1}
						showUploadList={false}
					>
						<div className="crm-square-upload-btn">
							<PlusOutlined style={{ fontSize: "18px", color: "var(--crm-ink-3)" }} />
							<span
								style={{
									fontSize: "11px",
									fontWeight: 500,
									color: "var(--crm-ink-2)",
									marginTop: "4px",
								}}
							>
								{label}
							</span>
						</div>
					</Upload>
				)}
			</div>
		</Form.Item>
	);
}

export function RoomCreatePage() {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const { role } = useOutletContext<{ role: AppRole }>();
	const queryClient = useQueryClient();
	const [form] = Form.useForm();
	const [planFile, setPlanFile] = useState<File>();
	const [image3dFile, setImage3dFile] = useState<File>();
	const [terraceFile, setTerraceFile] = useState<File>();

	const { data: buildings = [] } = useQuery({
		queryKey: ["buildings"],
		queryFn: () => buildingsApi.getBuildings({ limit: 100 }),
	});

	const initialBuildingId = location.state?.buildingId || searchParams.get("buildingId") || "";
	const initialBlockId = location.state?.blockId || searchParams.get("blockId") || "";
	const initialSectionId = location.state?.sectionId || searchParams.get("sectionId") || "";
	const [selectedBuildingId, setSelectedBuildingId] = useState<string>(initialBuildingId);
	const [selectedBlockId, setSelectedBlockId] = useState<string>(initialBlockId);

	const { data: blocks = [] } = useQuery({
		queryKey: ["blocks", selectedBuildingId],
		queryFn: () => blocksApi.getBlocks({ buildingId: selectedBuildingId, limit: 100 }),
		enabled: !!selectedBuildingId,
	});

	const { data: sections = [] } = useQuery({
		queryKey: ["sections", selectedBlockId],
		queryFn: () => sectionsApi.getSections({ blockId: selectedBlockId, limit: 100 }),
		enabled: !!selectedBlockId,
	});

	const createRoomMutation = useMutation({
		mutationFn: roomsApi.createRoom,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["rooms"] });
			message.success("Xona muvaffaqiyatli qo'shildi!");
		},
	});

	useEffect(() => {
		if (selectedBuildingId || buildings.length === 0) {
			return;
		}
		const firstBuildingId = buildings[0]?.id;
		setSelectedBuildingId(firstBuildingId ?? "");
		form.setFieldsValue({ buildingId: firstBuildingId, blockId: undefined, sectionId: undefined });
	}, [buildings, form, selectedBuildingId]);

	useEffect(() => {
		if (!selectedBuildingId || blocks.length === 0) {
			return;
		}
		const hasSelectedBlock = blocks.some((block) => block.id === selectedBlockId);
		if (hasSelectedBlock) {
			return;
		}
		const firstBlockId = blocks[0]?.id;
		setSelectedBlockId(firstBlockId ?? "");
		form.setFieldsValue({ blockId: firstBlockId, sectionId: undefined });
	}, [blocks, form, selectedBlockId, selectedBuildingId]);

	useEffect(() => {
		if (!initialSectionId || sections.length === 0) {
			return;
		}
		if (sections.some((section) => section.id === initialSectionId)) {
			form.setFieldsValue({ sectionId: initialSectionId });
		}
	}, [form, initialSectionId, sections]);

	const handleBuildingChange = (buildingId: string) => {
		setSelectedBuildingId(buildingId);
		const firstBlock = blocks.find((b) => b.buildingId === buildingId);
		if (firstBlock) {
			setSelectedBlockId(firstBlock.id);
			form.setFieldsValue({ blockId: firstBlock.id, sectionId: undefined });
		} else {
			setSelectedBlockId("");
			form.setFieldsValue({ blockId: undefined, sectionId: undefined });
		}
	};

	const handleBlockChange = (blockId: string) => {
		setSelectedBlockId(blockId);
		form.setFieldsValue({ sectionId: undefined });
	};

	const availableBlocks = blocks;
	const availableSections = sections.filter((s) => s.blockId === selectedBlockId);
	const selectedBlock = availableBlocks.find((block) => block.id === selectedBlockId);
	const maxFloor = selectedBlock?.floorsCount;

	if (role === "manager") {
		return <Navigate to="/rooms" replace />;
	}

	const handleSave = () => {
		form.validateFields().then(async (values) => {
			try {
				const pricePerArea = toBackendPricePerArea(values.basePrice);
				if (!pricePerArea) {
					message.error("1 m² narxini kiriting");
					form.scrollToField("basePrice");
					return;
				}
				const [plan, image3d, terrace] = await Promise.all([
					planFile ? filesApi.uploadFile(planFile, "image") : Promise.resolve(undefined),
					image3dFile ? filesApi.uploadFile(image3dFile, "image") : Promise.resolve(undefined),
					terraceFile ? filesApi.uploadFile(terraceFile, "image") : Promise.resolve(undefined),
				]);

				await createRoomMutation.mutateAsync(
					{
						sectionId: values.sectionId,
						roomNumber: values.roomNumber,
						floor: values.floor,
						roomsCount: parseInt(values.roomsCount, 10),
						area: values.area.toString(),
						terassaArea: values.terraceArea?.toString(),
						hasTerassa: Number(values.terraceArea || 0) > 0,
						pricePerArea,
						priceRemont: calculatePriceRemont({
							area: values.area,
							finishedPrice: values.finishedPrice,
							pricePerArea: values.basePrice,
						}),
						roomChoice: values.finishType,
						isKadastr: values.hasCadastre,
						isHighlighted: values.isHighlighted,
						...(plan?.url ? { image: plan.url } : {}),
						...(image3d?.url ? { image3d: image3d.url } : {}),
						...(terrace?.url ? { terassaImage: terrace.url } : {}),
					},
					{
						onSuccess: () => {
							navigate(
								`/rooms?buildingId=${values.buildingId}&blockId=${values.blockId}&sectionId=${values.sectionId}`,
								{ state: location.state }
							);
						},
					}
				);
			} catch (error) {
				message.error(parseApiError(error) || "Xona yaratishda xatolik yuz berdi");
			}
		});
	};

	return (
		<div className="crm-room-create" style={{ maxWidth: "800px" }}>
			<div className="crm-page-head">
				<div>
					<Button
						className="crm-back-link"
						icon={<LeftOutlined />}
						onClick={() => navigate("/rooms", { state: location.state })}
					>
						Xonalar xaritasi
					</Button>
					<h1>Xona qo'shish</h1>
					<p style={{ color: "var(--crm-ink-3)" }}>Yangi xona qo'shish ·</p>
				</div>
			</div>

			<div className="crm-card">
				<div className="crm-card-body">
					<Form
						form={form}
						layout="vertical"
						initialValues={{
							buildingId: selectedBuildingId,
							blockId: selectedBlockId,
							roomsCount: "2",
							finishType: "tamirsiz",
							terraceArea: 0,
							finishedPrice: 0,
							hasCadastre: false,
							isHighlighted: false,
						}}
					>
						{/* Location selection cascade */}
						<div className="crm-inline-fields">
							<Form.Item
								name="buildingId"
								label="Bino"
								rules={[{ required: true, message: "Binoni tanlang!" }]}
							>
								<Select onChange={handleBuildingChange}>
									{buildings.map((b) => (
										<Select.Option key={b.id} value={b.id}>
											{b.name}
										</Select.Option>
									))}
								</Select>
							</Form.Item>

							<Form.Item
								name="blockId"
								label="Blok"
								rules={[{ required: true, message: "Blokni tanlang!" }]}
							>
								<Select onChange={handleBlockChange}>
									{availableBlocks.map((b) => (
										<Select.Option key={b.id} value={b.id}>
											{b.name}
										</Select.Option>
									))}
								</Select>
							</Form.Item>

							<Form.Item
								name="sectionId"
								label="Sektsiya (padezd)"
								rules={[{ required: true, message: "Sektsiyani tanlang!" }]}
							>
								<Select placeholder="Tanlang...">
									{availableSections.map((s) => (
										<Select.Option key={s.id} value={s.id}>
											{s.name}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</div>

						<div className="crm-divider" />

						{/* Room details */}
						<div className="crm-inline-fields">
							<Form.Item
								name="roomNumber"
								label="Xona raqami"
								rules={[{ required: true, message: "Xona raqamini kiriting!" }]}
							>
								<Input placeholder="Masalan: 503" />
							</Form.Item>

							<Form.Item
								name="floor"
								label="Qavat"
								rules={[
									{ required: true, message: "Qavatni kiriting!" },
									{
										validator: (_, value) => {
											if (!(value && maxFloor) || value <= maxFloor) {
												return Promise.resolve();
											}
											return Promise.reject(
												new Error(`Tanlangan blokda maksimal ${maxFloor} qavat bor`)
											);
										},
									},
								]}
							>
								<InputNumber max={maxFloor} min={1} style={{ width: "100%" }} placeholder="5" />
							</Form.Item>

							<Form.Item
								name="roomsCount"
								label="Xonalar soni"
								rules={[{ required: true, message: "Xonalar sonini tanlang!" }]}
							>
								<Select>
									<Select.Option value="1">1-xonali</Select.Option>
									<Select.Option value="2">2-xonali</Select.Option>
									<Select.Option value="3">3-xonali</Select.Option>
									<Select.Option value="4">4-xonali</Select.Option>
								</Select>
							</Form.Item>
						</div>

						<div className="crm-inline-fields">
							<Form.Item
								name="area"
								label="Maydon (m²)"
								rules={[{ required: true, message: "Maydonni kiriting!" }]}
							>
								<InputNumber min={10} style={{ width: "100%" }} placeholder="72.54" />
							</Form.Item>

							<Form.Item name="terraceArea" label="Terrasa (m²)">
								<InputNumber min={0} style={{ width: "100%" }} placeholder="0" />
							</Form.Item>

							<Form.Item
								name="finishType"
								label="Ta'mir turi"
								rules={[{ required: true, message: "Ta'mir turini tanlang!" }]}
							>
								<Select>
									<Select.Option value="tamirsiz">Ta'mirsiz (qora suvoq)</Select.Option>
									<Select.Option value="tamirli">Ta'mirli (tayyor)</Select.Option>
								</Select>
							</Form.Item>
						</div>

						<div className="crm-inline-fields">
							<Form.Item
								name="basePrice"
								label="1 m² narxi (UZS)"
								rules={[{ required: true, message: "1 m² narxini kiriting!" }]}
							>
								<InputNumber<number>
									min={1}
									style={{ width: "100%" }}
									placeholder="7 500 000"
									formatter={(value) => `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
									parser={(value) => Number((value ?? "").replace(/\s/g, ""))}
								/>
							</Form.Item>

							<Form.Item name="finishedPrice" label="Ta'mirli umumiy narx (UZS)">
								<InputNumber<number>
									min={0}
									style={{ width: "100%" }}
									placeholder="0"
									formatter={(value) => `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
									parser={(value) => Number((value ?? "").replace(/\s/g, ""))}
								/>
							</Form.Item>
						</div>

						<div
							className="row gap-l crm-room-checks"
							style={{ display: "flex", gap: "20px", marginBottom: "20px", marginTop: "8px" }}
						>
							<Form.Item name="hasCadastre" valuePropName="checked" noStyle>
								<Checkbox>Kadastrlashtirilgan</Checkbox>
							</Form.Item>
							<Form.Item name="isHighlighted" valuePropName="checked" noStyle>
								<Checkbox>Xaritada ajratib ko'rsatish</Checkbox>
							</Form.Item>
						</div>

						<div className="crm-room-upload-grid" style={{ marginBottom: "20px" }}>
							<RoomUploadField file={planFile} label="Xona plani" onFileChange={setPlanFile} />
							<RoomUploadField
								file={image3dFile}
								label="3D ko'rinish"
								onFileChange={setImage3dFile}
							/>
							<RoomUploadField
								file={terraceFile}
								label="Terrasa plani"
								onFileChange={setTerraceFile}
							/>
						</div>

						<div
							className="crm-card"
							style={{ background: "var(--crm-amber-soft)", borderColor: "#f0dcae" }}
						>
							<div
								className="crm-card-body row center gap-s muted"
								style={{
									display: "flex",
									gap: "8px",
									alignItems: "center",
									color: "var(--crm-ink-2)",
									fontSize: "12px",
									padding: "12px",
								}}
							>
								<InfoCircleOutlined style={{ color: "var(--crm-amber)" }} />
								<span>
									Holat (Sotuvda/Sotilgan) bu yerda belgilanmaydi — u faqat sotuv jarayoni orqali
									o'zgaradi. Sotilgan xonada maydon yoki qavatni o'zgartirib bo'lmaydi.
								</span>
							</div>
						</div>

						<div
							className="crm-form-actions"
							style={{ display: "flex", gap: "8px", marginTop: "20px", justifyContent: "flex-end" }}
						>
							<Button onClick={() => navigate("/rooms", { state: location.state })}>
								Bekor qilish
							</Button>
							<Button
								className="crm-primary-btn"
								onClick={handleSave}
								loading={createRoomMutation.isPending}
							>
								Saqlash
							</Button>
						</div>
					</Form>
				</div>
			</div>
		</div>
	);
}
